<script setup lang="ts">
/**
 * Quick-add IOC form — the primary Oweis surface.
 *
 * Design constraint (E14 §1): must be measurably faster than the spreadsheet
 * workflow Oweis has open in another window. If a trained user takes more
 * than ~5 seconds to ingest one IOC, this form has failed its purpose.
 *
 * How it achieves that:
 *   - Autofocuses the Value input on mount
 *   - Remembers last-used type across submissions (localStorage)
 *   - Ctrl+Enter saves; Ctrl+Shift+Enter saves and opens a fresh form
 *   - "Save & Add Another" keeps Type, clears Value, refocuses
 *   - Smart defaults on every algorithm-owned field so Oweis never touches
 *     fidelity/tlp/severity unless he wants to
 *   - Three visible fields by default; "More fields" is collapsed but one
 *     click away for the ~10% of cases that need detail
 *
 * All saves hit POST /api/threats/indicators (the E13 tenant CRUD endpoint).
 */
import { ref, onMounted, nextTick } from 'vue';
import apiClient from '@/api/client';
import QuickAddScopeLabel from './QuickAddScopeLabel.vue';
import { ChevronDown, ChevronRight } from 'lucide-vue-next';

const emit = defineEmits<{ done: [] }>();

const LAST_TYPE_KEY = 'cwr:qa:last-ioc-type';

// Default to 'domain' because it's the most common IOC type for Oweis's
// research workflow (OSINT / VirusTotal feeds are domain-heavy).
const type = ref<string>(localStorage.getItem(LAST_TYPE_KEY) || 'domain');
const value = ref('');
const notes = ref('');

// Collapsed by default. Once toggled open, stays open for the rest of the
// modal session so Save & Add Another doesn't surprise the user.
const moreOpen = ref(false);

const saving = ref(false);
const errorMsg = ref<string | null>(null);
const lastSavedValue = ref<string | null>(null);

// Optional "More fields" with sensible defaults.
const fidelity = ref(50);
const tlp = ref('amber');
const severity = ref('medium');
const source = ref('analyst');

const valueInput = ref<HTMLInputElement | null>(null);

onMounted(async () => {
  await nextTick();
  valueInput.value?.focus();
});

function resetForNextEntry() {
  lastSavedValue.value = value.value.trim();
  value.value = '';
  notes.value = '';
  errorMsg.value = null;
}

async function save(addAnother: boolean) {
  if (!value.value.trim()) return;
  saving.value = true;
  errorMsg.value = null;

  try {
    await apiClient.post('/indicators', {
      type: type.value,
      value: value.value.trim(),
      description: notes.value.trim() || undefined,
      source: source.value,
      fidelity: fidelity.value,
      tlp: tlp.value,
      status: 'active',
      severity: severity.value,
    });

    // Remember type for the next session. Only after a successful save so
    // we don't stick a broken choice.
    localStorage.setItem(LAST_TYPE_KEY, type.value);

    if (addAnother) {
      resetForNextEntry();
      await nextTick();
      valueInput.value?.focus();
    } else {
      emit('done');
    }
  } catch (err: unknown) {
    const resp = (err as { response?: { data?: { message?: string | string[] } } })?.response;
    const m = resp?.data?.message;
    errorMsg.value = Array.isArray(m) ? m[0] : m ?? 'Save failed';
  } finally {
    saving.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  // Ctrl+Enter (or Cmd+Enter) submits. Shift variant submits + keeps the
  // overlay open for another entry.
  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
    e.preventDefault();
    save(e.shiftKey);
  }
}
</script>

<template>
  <form
    class="quick-add-ioc-form flex flex-col gap-3"
    data-qa-form="indicator"
    @keydown="onKeydown"
    @submit.prevent="save(false)"
  >
    <div class="quick-add-ioc-header flex flex-col gap-1">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-text-primary">Add Indicator</h2>
        <QuickAddScopeLabel />
      </div>
      <p v-if="lastSavedValue" class="text-xs text-status-success">
        Saved: {{ lastSavedValue }}
      </p>
    </div>

    <div v-if="errorMsg" class="quick-add-ioc-error text-sm text-status-error">
      {{ errorMsg }}
    </div>

    <div class="quick-add-ioc-fields flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Type</label>
        <select v-model="type" class="qa-input" data-qa-field="type">
          <option value="ipv4">IPv4</option>
          <option value="ipv6">IPv6</option>
          <option value="domain">Domain</option>
          <option value="url">URL</option>
          <option value="email">Email</option>
          <option value="md5">MD5</option>
          <option value="sha1">SHA1</option>
          <option value="sha256">SHA256</option>
          <option value="filename">Filename</option>
          <option value="registry_key">Registry Key</option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Value</label>
        <input
          ref="valueInput"
          v-model="value"
          class="qa-input font-mono"
          placeholder="evil.example.com"
          data-qa-field="value"
          autocomplete="off"
          spellcheck="false"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Notes (optional)</label>
        <input
          v-model="notes"
          class="qa-input"
          placeholder="Where you saw it, why it matters"
          data-qa-field="notes"
          autocomplete="off"
        />
      </div>
    </div>

    <button
      type="button"
      class="quick-add-ioc-more-toggle flex items-center gap-1 text-xs text-text-muted self-start hover:text-text-secondary cwr-motion"
      @click="moreOpen = !moreOpen"
    >
      <component :is="moreOpen ? ChevronDown : ChevronRight" class="w-3 h-3" />
      More fields
    </button>

    <div
      v-if="moreOpen"
      class="quick-add-ioc-more grid grid-cols-2 gap-2 pt-2 border-t border-border-default"
    >
      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Fidelity (0-100)</label>
        <input v-model.number="fidelity" type="number" min="0" max="100" class="qa-input" />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">TLP</label>
        <select v-model="tlp" class="qa-input">
          <option value="white">White</option>
          <option value="green">Green</option>
          <option value="amber">Amber</option>
          <option value="red">Red</option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Severity</label>
        <select v-model="severity" class="qa-input">
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="info">Info</option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Source</label>
        <input v-model="source" class="qa-input" />
      </div>
    </div>

    <div class="quick-add-ioc-actions flex items-center justify-end gap-2 pt-2">
      <button type="button" class="qa-btn-ghost" @click="emit('done')">Cancel</button>
      <button
        type="button"
        class="qa-btn-secondary"
        :disabled="!value.trim() || saving"
        data-qa-action="save-and-add"
        @click="save(true)"
      >
        Save &amp; Add Another
      </button>
      <button
        type="submit"
        class="qa-btn-primary"
        :disabled="!value.trim() || saving"
        data-qa-action="save"
      >
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </div>

    <div class="quick-add-ioc-hint text-[10px] text-text-disabled pt-1">
      <kbd class="font-mono">ctrl+enter</kbd> save ·
      <kbd class="font-mono">ctrl+shift+enter</kbd> save &amp; add another ·
      <kbd class="font-mono">esc</kbd> close
    </div>
  </form>
</template>

<style scoped>
.qa-input {
  background: var(--color-surface-700, #2a2a3e);
  border: 1px solid var(--color-border-default, #3a3a4e);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: var(--color-text-primary, #e5e5ea);
  font-size: 0.875rem;
  outline: none;
}
.qa-input:focus {
  border-color: var(--color-accent-blue, #3b82f6);
}
.qa-btn-primary {
  background: var(--color-accent-blue, #3b82f6);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}
.qa-btn-primary:hover:not(:disabled) {
  opacity: 0.9;
}
.qa-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.qa-btn-secondary {
  background: var(--color-surface-700, #2a2a3e);
  color: var(--color-text-primary, #e5e5ea);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid var(--color-border-default, #3a3a4e);
}
.qa-btn-secondary:hover:not(:disabled) {
  background: var(--color-surface-600, #3a3a4e);
}
.qa-btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.qa-btn-ghost {
  color: var(--color-text-secondary, #a0a0aa);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}
.qa-btn-ghost:hover {
  background: var(--color-surface-700, #2a2a3e);
}
</style>
